import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHero } from "@/components/common";
import { CoachTip } from "@/components/HelpHint";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronDown, Info, FileText, FileSpreadsheet, Gift } from "lucide-react";

const gbp = (n) => `£${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Cat = ({ title, total, children, testid }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3">
        <span className="font-semibold text-[#0B1A2F]">{title}</span>
        <span className="flex items-center gap-3"><span className="font-bold text-[#0B1A2F]">{gbp(total)}</span><ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && <div className="border-t border-slate-100 divide-y divide-slate-50">{children}</div>}
    </div>
  );
};
const Row = ({ label, value, sub }) => (
  <div className="flex items-center justify-between px-5 py-2.5 text-sm">
    <span className="text-slate-700">{label}{sub && <span className="block text-xs text-slate-400">{sub}</span>}</span>
    <span className="font-medium text-[#0B1A2F]">{value}</span>
  </div>
);

export default function MyTotalReward() {
  const { id } = useParams();
  const { user } = useAuth();
  const empId = id || user?.employee_id;
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { if (empId) api.get(`/reward/calculate/${empId}`).then((r) => setD(r.data)).catch(() => setErr(true)); }, [empId]);

  if (err) return (
    <div className="space-y-6" data-testid="my-total-reward">
      <PageHero title="My Total Reward" subtitle="Your pay and benefits at a glance" icon={Gift} eyebrow="Benefits" pageKey="benefits" />
      <CoachTip tipKey="benefits" title="More than your salary">Your Total Reward adds up salary, allowances, employer-funded benefits and extra paid leave. Export a PDF any time.</CoachTip>
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500" data-testid="reward-error">
        <Info size={26} className="mx-auto mb-2 text-slate-300" />
        Your Total Reward statement has not been published yet. Please check back once HR has released it.
      </div>
    </div>
  );
  if (!d) return <div className="text-slate-400" data-testid="reward-loading">Loading your Total Reward…</div>;
  const t = d.totals;
  const exp = (fmt) => window.open(`${API}/reward/statement/${empId}/export?format=${fmt}`, "_blank");

  return (
    <div className="space-y-6" data-testid="my-total-reward">
      <PageHero title="My Total Reward" subtitle={`Statement period ${fmtDate(d.period.start)} – ${fmtDate(d.period.end)} · as at ${fmtDate(d.as_at)}`} icon={Gift} eyebrow="Benefits" pageKey="benefits">
        <Button size="sm" className="bg-white text-[#0B1A2F] hover:bg-slate-100" onClick={() => exp("pdf")} data-testid="my-reward-export-pdf"><FileText size={15} className="mr-1" />PDF</Button>
        <Button size="sm" className="bg-white text-[#0B1A2F] hover:bg-slate-100" onClick={() => exp("xlsx")} data-testid="my-reward-export-xlsx"><FileSpreadsheet size={15} className="mr-1" />Excel</Button>
      </PageHero>

      <CoachTip tipKey="benefits" title="More than your salary">Your Total Reward adds up salary, allowances, employer-funded benefits and extra paid leave. Export a PDF any time.</CoachTip>

      <div className="bg-white border border-slate-200 border-t-4 border-t-[#2EA3FD] rounded-2xl shadow-sm p-6" data-testid="reward-headline">
        <p className="text-xs uppercase tracking-wider text-slate-400">Your indicative total reward</p>
        <p className="text-4xl font-extrabold mt-1 text-[#0B1A2F]">{gbp(t.indicative_total)}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          {[["Basic salary", d.salary.annual], ["Cash allowances", t.cash_package - d.salary.annual], ["Employer-funded benefits", t.employer_benefits], ["Additional paid leave", t.additional_leave]].map(([l, v]) => (
            <div key={l} className="bg-slate-50 rounded-lg px-3 py-2"><p className="text-[11px] text-slate-500">{l}</p><p className="text-lg font-bold text-[#0B1A2F]">{gbp(v)}</p></div>
          ))}
        </div>
      </div>

      {d.warnings?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 flex gap-2" data-testid="reward-warnings"><AlertTriangle size={16} className="shrink-0 mt-0.5" /><ul className="list-disc pl-4">{d.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul></div>
      )}

      <Cat title="Salary" total={d.salary.annual} testid="reward-salary">
        <Row label="Basic annual salary" value={gbp(d.salary.annual)} sub={d.salary.effective_from ? `Effective ${fmtDate(d.salary.effective_from)}` : null} />
        <Row label="Monthly equivalent" value={gbp(d.salary.monthly)} />
      </Cat>

      {d.cash_allowances.length > 0 && (
        <Cat title="Cash allowances" total={t.cash_package - d.salary.annual} testid="reward-cash">
          {d.cash_allowances.map((c, i) => <Row key={i} label={c.name} value={gbp(c.annual_value)} sub={c.taxable ? "This benefit may be subject to tax." : null} />)}
        </Cat>
      )}

      {d.employer_benefits.length > 0 && (
        <Cat title="Employer-funded benefits" total={t.employer_benefits} testid="reward-benefits">
          {d.employer_benefits.map((b, i) => <Row key={i} label={b.name} value={gbp(b.annual_value)} sub={b.taxable ? "This benefit may be subject to tax. The value shown is not a calculation of your personal tax liability." : null} />)}
        </Cat>
      )}

      {d.additional_leave.length > 0 && (
        <Cat title="Additional paid leave (illustrative)" total={t.additional_leave} testid="reward-leave">
          {d.additional_leave.map((a, i) => (
            <div key={i} className="px-5 py-2.5 text-sm">
              <div className="flex items-center justify-between"><span className="text-slate-700">{a.name}</span><span className="font-medium text-[#0B1A2F]">{gbp(a.annual_value)}</span></div>
              {a.dates?.map((x, j) => <div key={j} className="flex justify-between text-xs text-slate-400 pl-3"><span>{x.label || "Shutdown"} · {fmtDate(x.date)} · {x.hours}h</span><span>{gbp(x.value)}</span></div>)}
              {(!a.dates || a.dates.length === 0) && <p className="text-xs text-slate-400 pl-3">Not applicable this period</p>}
            </div>
          ))}
        </Cat>
      )}

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-sm text-slate-600 space-y-2" data-testid="reward-explainer">
        <p className="font-semibold text-[#0B1A2F] flex items-center gap-2"><Info size={15} className="text-[#2EA3FD]" />How this is calculated</p>
        <p>Your Indicative Total Reward brings together your salary, cash allowances, employer-funded benefits and the estimated value of additional paid leave provided by CASS.</p>
        <p>Additional paid-leave values are illustrative. Your salary already continues while you take this leave, and this amount is not an additional cash payment. Additional leave is valued using your normal scheduled working hours on each applicable date and the salary effective on that date. Weekends, bank holidays and ordinary holiday entitlement are excluded.</p>
        <p className="font-medium text-[#0B1A2F]">This figure is not your take-home pay. The application does not calculate tax, National Insurance or payroll.</p>
      </div>
    </div>
  );
}
